<!-- @format -->

<template>
	<disabled-text v-if="disabled && pcProps.showDisabledText" :value="value" :params="pcProps.params" />
	<el-tree-select
		v-else
		v-model="value"
		:data="params"
		:disabled="disabled"
		:render-after-expand="false"
		clearable
		filterable
		style="width: 100%"
		:check-strictly="pcProps.checkStrictly"
		@change="handelChange"
	/>
</template>

<script setup lang="ts">
	import { type WidgetConfigType } from '../../types';
	import { flat } from '../../utils/util';
	import { paramsTypeEnum } from '../../config';
	import { getCheckRowsToFormDatas } from '../../utils/user';
	import DisabledText from './disabledText.vue';
	import { getCache, setCache } from '@/utils/util';
	import { getDataProcessList } from '@/api/factory';
	import { useDictList } from '@/api';

	interface Props {
		modelValue: any;
		pcProps: WidgetConfigType;
		disabled?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		modelValue: '',
	});

	const widgetDatas = inject<any>('widgetDatas', ref([]));
	const emit = defineEmits(['update:modelValue']);
	const flatWigetDatas = computed(() => flat(widgetDatas.value || []));
	const attr = useAttrs();
	const setFieldsValue = inject<(fields: Record<string, any>) => void>('setFieldsValue', () => {});

	const value = computed({
		get() {
			if (attr.multiple) {
				return props.modelValue ? props.modelValue.split(',') : [];
			}
			return props.modelValue;
		},
		set(val) {
			emit('update:modelValue', val ? val?.toString() : '');
		},
	});

	const dictList = ref<any>([]);

	const params = computed(() =>
		props.pcProps.paramsType &&
		(props.pcProps.paramsType === paramsTypeEnum.引用 || props.pcProps.paramsType === paramsTypeEnum.业务对象)
			? dictList.value
			: props.pcProps.params,
	);

	const getDictList = async (dictType: string) => {
		const cacheRes = getCache('dictCache', dictType);
		if (cacheRes) {
			dictList.value = cacheRes;
		} else {
			const res = await useDictList({ dictType });
			setCache('dictCache', dictType, res);
			dictList.value = res;
		}
	};

	const getDataProcessListData = async (newObjectType, newObjectServer) => {
		const res: any = await getDataProcessList({
			businessCode: newObjectType,
			url: newObjectServer,
		});
		dictList.value = res.map((item) => ({
			...item,
			value: props.pcProps.objectValue ? item[props.pcProps.objectValue] : '',
			label: props.pcProps.objectLabel ? item[props.pcProps.objectLabel] : '',
		}));
	};

	const handelChange = (val) => {
		if (props.pcProps.paramsType === paramsTypeEnum.业务对象) {
			if (props.pcProps.linkConfig && props.pcProps.linkConfig.length > 0) {
				const data: any = [];
				if (attr.multiple) {
					dictList.value.forEach((obj) => {
						if (val.includes(obj.value)) {
							data.push(obj);
						}
					});
				} else {
					data.push(dictList.value.find((obj) => obj.value === val));
				}
				const formDatas = getCheckRowsToFormDatas(props.pcProps.linkConfig, flatWigetDatas.value, data);
				setFieldsValue && setFieldsValue(formDatas);
			}
		}
	};

	watch(
		[
			() => props.pcProps.citeType,
			() => props.pcProps.objectType,
			() => props.pcProps.objectServer,
			() => props.pcProps.objectValue,
			() => props.pcProps.objectLabel,
		],
		([newCiteType, newObjectType, newObjectServer, newObjectLabel, newObjectValue]) => {
			if (props.pcProps.paramsType === paramsTypeEnum.引用) {
				newCiteType && getDictList(newCiteType);
			} else if (props.pcProps.paramsType === paramsTypeEnum.业务对象) {
				newObjectType &&
					newObjectServer &&
					newObjectValue &&
					newObjectLabel &&
					getDataProcessListData(newObjectType, newObjectServer);
			} else {
				dictList.value = props.pcProps.params;
			}
		},
		{
			immediate: true,
		},
	);
</script>

<style scoped></style>
